import { lazy, Suspense } from "react"
import { createRootRouteWithContext, Outlet } from "@tanstack/react-router"

import { RouterContext } from "../main"
import { DialogQueueProvider } from "@src/context/DialogQueueProvider"
import { CookieConsent } from "@src/components/organisms/CookieConsent"
import { LanguageApplicationWrapper } from "@src/context/Languages"
import { getPublicTenantSettingsQuery } from "@src/api/tenantSettings"
import { useAppStore } from "@src/state"

// Only load Tanstack router devtools in development mode
const LazyTanStackRouterDevtools = import.meta.env.PROD
  ? () => null
  : lazy(() =>
      import("@tanstack/router-devtools").then((res) => ({
        default: res.TanStackRouterDevtools,
      })),
    )

export const Route = createRootRouteWithContext<RouterContext>()({
  // Runs before any child beforeLoad, so the SAML SSO token is in the store
  // before /_auth gates the route on authentication.
  beforeLoad: ({ context }) => {
    const params = new URLSearchParams(window.location.search)
    const token = params.get("token")
    if (token) {
      context.useAppStore.getState().setToken(token)
      const url = new URL(window.location.href)
      url.searchParams.delete("token")
      window.history.replaceState({}, "", url.toString())
    }
  },
  loader: async ({ context }) => {
    try {
      const data = await context.queryClient.ensureQueryData(
        getPublicTenantSettingsQuery(),
      )
      const extensions = (data.allowedFileExtensions ?? "")
        .split(",")
        .map((ext) => ext.trim().toLowerCase().replace(/^\./, ""))
        .filter(Boolean)
      useAppStore.getState().setAllowedFileExtensions(extensions)
    } catch {
      // If fetch fails, app continues with empty array (no validation blocks uploads)
    }
  },
  // Wrapping the root route in the dialog provider because in the main.tsx the dialog provider
  // would wrap the Router one and any router methods could not be used in dialogs
  component: RootComponent,
})

function RootComponent() {
  return (
    <LanguageApplicationWrapper>
      <DialogQueueProvider>
        <Outlet />
        <CookieConsent />
        <Suspense>
          <LazyTanStackRouterDevtools initialIsOpen={false} />
        </Suspense>
      </DialogQueueProvider>
    </LanguageApplicationWrapper>
  )
}
